import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import FilterPanelCheckboxGroupItem from 'components/Common/FilterPanel/FilterPanelCheckboxGroupItem'
import { buildPanelCheckItems } from 'components/Cruises/SearchPage/Filters/Inputs/CruiseExtraFiltersInputs'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import useToggle from 'hooks/useToggle'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { CRUISE_LINE_CUSTOM_INFO } from 'constants/cruise'
import { sortBy, sum } from 'lib/array/arrayUtils'
import Accordion from 'LuxKit/Accordion'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import useQueryParams from 'hooks/useQueryParams'
import { parseCruiseLinesFromUrl } from 'lib/cruises/cruiseUtils'

import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

interface Props {
  shipItems: Array<App.CruiseSearchFacet>
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  defaultSelected?: Set<string>
  fetching?: boolean
}

function CruiseShipCheckboxGroup({
  shipItems,
  onChange,
  defaultSelected,
  fetching,
}: Props) {
  const intl = useIntl()
  const globalSearchState = useContext(GlobalSearchStateContext)
  const { cruiseLines: searchCruiseLines = [] } = globalSearchState
  const [cruiseLines] = useCruiseSearchFacets({
    facetTypes: ['cruise_lines'],
    offerSource: 'cruises',
  })
  const queryParams = useQueryParams()

  const cruiseLineNames = useMemo(() => {
    // If all cruise lines are selected, return an empty array
    // used to keep all cruise lines closed if all are selected
    if (searchCruiseLines.length === cruiseLines.length) {
      return []
    }

    return cruiseLines
      .filter(({ value }) => searchCruiseLines.includes(value as string))
      .map(({ name }) => name)
  }, [searchCruiseLines, cruiseLines])

  const [limit, setLimit] = useState(10)
  const [showMore, toggleShowMore] = useToggle(defaultSelected?.size! > 0)
  const [cruiseShipSearch, setCruiseShipSearch] = useState<string>()
  const [cruiseLinesOpen, setCruiseLinesOpen] = useState({} as Record<string, boolean>)

  const vendorsGroupedByVendorName = useMemo(() => {
    return shipItems.reduce((acc, shipItem) => {
      const vendorName = shipItem.vendorName || ' '
      if (cruiseShipSearch && !shipItem.name.toLowerCase().includes(cruiseShipSearch.toLowerCase())) {
        return acc
      }

      if (!acc[vendorName]) {
        acc[vendorName] = [shipItem]
      } else {
        acc[vendorName].push(shipItem)
      }
      return acc
    }, {} as Record<string, Array<App.CruiseSearchFacet>>)
  }, [shipItems, cruiseShipSearch])

  const openCruiseLine = useMemo(() => {
    return shipItems.reduce((acc, shipItem) => {
      const vendorName = shipItem.vendorName || ' '
      acc[vendorName] = false
      return acc
    }, {} as Record<string, boolean>)
  }, [shipItems])

  const setItemsCruiseLinesOpen = useCallback((items: Record<string, boolean>, open: boolean) => {
    const newCruiseLineOpen = {} as Record<string, boolean>
    Object.keys(items).forEach((key) => {
      newCruiseLineOpen[key] = open
    })
    setCruiseLinesOpen(newCruiseLineOpen)
  }, [])

  const countAvailableShips = useCallback((ships: Array<App.CruiseSearchFacet>) => {
    return sum(ships, (ship) => (ship?.count || 0) + (ship?.flashOfferCount! || 0))
  }, [])

  useEffect(() => {
    const cruiseLines = parseCruiseLinesFromUrl(queryParams.get('cruiseLines'))
    if (defaultSelected?.size! > 0 || cruiseLineNames.length || cruiseLines.length) {
      setItemsCruiseLinesOpen(openCruiseLine, true)
      if (!showMore) {
        toggleShowMore()
      }
    } else {
      setCruiseLinesOpen(openCruiseLine)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sortedShips = useMemo(() => {
    Object.entries(vendorsGroupedByVendorName).forEach(([key, ships]) => {
      vendorsGroupedByVendorName[key] = sortBy(ships, ({ count }) => count, 'desc')
    })
    return Object.entries(vendorsGroupedByVendorName)
  }, [vendorsGroupedByVendorName])

  const sortedVendorsByShips = useMemo(() => {
    return sortBy(sortedShips, ([, ships]) => {
      return countAvailableShips(ships)
    }, 'desc')
  }, [countAvailableShips, sortedShips])

  const mappedShips = useMemo(() => {
    return sortedVendorsByShips
      .map(([key, ships]) => {
        const vendorCode = ships.length ? ships[0].code : ''
        return {
          vendorName: key,
          show: false,
          imageId: CRUISE_LINE_CUSTOM_INFO.find(({ code }) => code === vendorCode)?.imageIdSm,
          ships: buildPanelCheckItems(ships),
          countOffers: countAvailableShips(ships),
        }
      }).filter(Boolean)
  }, [countAvailableShips, sortedVendorsByShips])

  const setItemOpen = useCallback((vendorName: string) => () => {
    setCruiseLinesOpen((prev) => {
      return {
        ...prev,
        [vendorName]: !prev[vendorName],
      }
    })
  }, [])

  const onToggleShowMore = useCallback(() => {
    if (!showMore) {
      setLimit(Number.POSITIVE_INFINITY)
    } else {
      setLimit(10)
    }
    toggleShowMore()
  }, [showMore, toggleShowMore])

  const spliceShips = useMemo(() => {
    return showMore ? mappedShips : mappedShips.slice(0, limit)
  }, [showMore, mappedShips, limit])

  const canShowMore = useMemo(() => {
    return mappedShips.length > 10
  }, [mappedShips.length])

  const findShip = useCallback((val: string) => {
    setCruiseShipSearch(val)
    setItemsCruiseLinesOpen(cruiseLinesOpen, !!val)
  }, [cruiseLinesOpen, setItemsCruiseLinesOpen])

  // If there is only one ship, open the accordion by default
  useEffect(() => {
    if (spliceShips.length === 1) {
      setCruiseLinesOpen((prev) => {
        return { ...prev, [spliceShips[0].vendorName]: true }
      })
    }
  }, [spliceShips])

  const searchCruiseShipsPlaceholder = intl.formatMessage({
    id: 'T4wZ0p8T4u1JAHVYt5QO',
    defaultMessage: 'Search cruise ships',
    description: 'Placeholder text for searching cruise ships in filter',
  })

  const showLessLabel = intl.formatMessage({
    id: 'Ot37ElVcGMAl3ynevAP8',
    defaultMessage: 'Show less',
    description: 'Button text to collapse cruise ship list in the cruise section',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'DSLrAN57ysUigLa7lBRb',
    defaultMessage: 'Show more',
    description: 'Button text to expand cruise ship list in the cruise offer or booking section',
  })

  return <Group direction="vertical" gap={16} data-testid="cruise-ship-checkbox-group">
    {fetching && <>
      <FilterPanelLoader type="checkbox" />
      <FilterPanelLoader type="checkbox" />
      <FilterPanelLoader type="checkbox" />
    </>}
    {!fetching && <>
      <FilterSearchInput
        data-testid="cruise-ship-checkbox-group-search"
        placeholder={searchCruiseShipsPlaceholder}
        onChange={findShip}
        value={cruiseShipSearch}
      />
      <div>
        {spliceShips.map((item, index) => <Accordion
          key={index}
          data-testid="cruise-ship-checkbox-group-vendor-name"
          heading={item.vendorName}
          startIcon={<ResponsiveImage
            id={item?.imageId!}
            src={item?.imageId}
            width={25}
            quality="eco"
            role="presentation"
          />}
          endLabel={`(${item.countOffers})`}
          onClick={setItemOpen(item.vendorName)}
          hideDivider
          expanded={cruiseLinesOpen[item.vendorName]}
          size="small"
        >
          <Group direction="vertical" gap={16}>
            {!!item?.ships?.length && item?.ships?.map((ship, indexB) => <div
              key={indexB}
              data-testid="cruise-ship-checkbox-group-ship-name"
            >
              <FilterPanelCheckboxGroupItem
                item={ship}
                name="cruiseShips"
                onChange={(e) => {
                  trackClientEvent({
                    subject: 'filter_applied',
                    action: e.target.checked ? 'selected' : 'unselected',
                    category: 'cruise_ship_filter',
                    type: 'interaction',
                  })

                  trackEvent(searchEventWithContext({
                    searchEvent: { type: 'filters' },
                    sortContext: { value: 'recommended' },
                    searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
                    filterContexts: [{
                      label: e.target.value,
                      type: 'cruise_ship',
                      value: e.target.checked ? 'selected' : 'unselected',
                    }],
                  }))

                  if (onChange) {
                    onChange(e)
                  }
                }}
                defaultChecked={defaultSelected?.has(ship.value as string)}
              />
            </div>)}
          </Group>
        </Accordion>)}
      </div>
      {canShowMore && <SmallToggleButton
        data-testid="cruise-ship-checkbox-group-show-more"
        on={showMore}
        onClick={onToggleShowMore}
        size="medium"
        iconPosition="start"
      >
        {showMore ? showLessLabel : showMoreLabel}
      </SmallToggleButton>}
    </>}
  </Group>
}

export default CruiseShipCheckboxGroup
